import httpClient from '@/utils/httpClient'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { Account, Workspace } from '@/questionnaire/types'
const ENV = import.meta.env
const RUNTIME_ENV = {
  ...ENV,
  ...((globalThis as { import_meta_env?: Record<string, string | boolean | undefined> })?.import_meta_env
    ? (globalThis as { import_meta_env: Record<string, string | boolean | undefined> }).import_meta_env
    : {}),
}

export const useLsGeneralStore = defineStore('lsApplication', () => {
  const JWT_STORAGE_KEY = '__jwt'

  const env = ref(RUNTIME_ENV)
  const jwt = ref<string | null>(localStorage.getItem(JWT_STORAGE_KEY))
  const account = ref<Account | null>(null)
  const workspace = ref<Workspace>()
  const documentPanelWidth = ref<number>()

  class LoginFromTokenError extends Error {
    constructor(message: string = 'Failed to login from token') {
      super(message)
      this.name = 'LoginFromTokenError'
    }
  }

  httpClient.defaults.headers.common['Authorization'] = jwt.value

  type LoginData = {
    account: Account
    workspace: Workspace & { role: string }
    message?: string
  }

  const login = async (email: string, password: string): Promise<LoginData> => {
    const loginData = await httpClient.post<LoginData>('/v1/accounts/sign_in', { account: { email, password } })
    account.value = loginData.data.account
    workspace.value = loginData.data.workspace
    const newJwt = loginData.headers.authorization
    setJwt(newJwt)
    return loginData.data
  }

  const setJwt = (token: string) => {
    jwt.value = token
    localStorage.setItem(JWT_STORAGE_KEY, token)
    httpClient.defaults.headers.common['Authorization'] = token
  }

  const logout = async () => {
    await httpClient.delete('/v1/accounts/sign_out')
    forceLogout()
  }

  const forceLogout = () => {
    jwt.value = null
    account.value = null
    localStorage.removeItem(JWT_STORAGE_KEY)
    delete httpClient.defaults.headers.common['Authorization']
  }

  const loginFromToken = async () => {
    if (loggedIn.value) {
      const accountData = await httpClient.get<LoginData>('/v1/settings/me').catch(error => {
        console.error(error)
        forceLogout()
        throw new LoginFromTokenError()
      })
      account.value = accountData.data.account
      workspace.value = accountData.data.workspace
    }
  }

  const registerAccount = async (accountData: Omit<Account, 'id'>) => {
    return await httpClient.post('/v1/accounts', { account: accountData })
  }

  const loggedIn = computed(() => {
    const loggedOut = jwt.value === null || jwt.value === JSON.stringify(null)
    return !loggedOut
  })

  const setEnv = (newEnv: object) => {
    if (Object.keys(newEnv).length && newEnv.constructor === Object) {
      env.value = { ...ENV, ...newEnv, ...RUNTIME_ENV }
    }
  }

  return {
    // state
    env,
    jwt,
    account,
    workspace,
    documentPanelWidth,
    // computed
    loggedIn,
    // actions
    registerAccount,
    setEnv,
    logout,
    login,
    loginFromToken,
    forceLogout,
    setJwt,
  }
})
